import { Box, Chip, Link as MuiLink, Table as MuiTable, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { ReactElement, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { QueryFoundItem } from '../../api/schema';
import { ProductThumbnail } from '../../component/categoryIcon/CategoryIcon';
import LinearProgress from '../../component/linearProgress/LinearProgress';
import { InventoryListQuery } from '../../redux/slices/inventory/inventoryInterface';
import { foundItemsFetching, getFoundItems, listFoundItems } from '../../redux/slices/inventory/inventorySlice';
import { useDispatch } from '../../redux/store';
import { Card } from '../../styled/components';
import { savedWhen } from './Inventories';

const FOUND_LIMIT = 200;

// "Vehicle · Truck 3", "Job site · 12 Main St", "Home · Office".
export const foundWhere = (row: QueryFoundItem): string => {
  if (row.where === 'vehicle') {
    return row.vehicle_name ? `Vehicle · ${row.vehicle_name}` : 'Vehicle';
  }
  const label = row.where === 'job_site' ? 'Job site' : 'Home';
  return row.site_name ? `${label} · ${row.site_name}` : label;
};

type FoundItemsProps = { query: InventoryListQuery };

// Lost items a scan saw again, newest first, with the inventory page's site and
// date filters. "Unexpected site" marks a job site the item was never left at.
export const FoundItems = ({ query }: FoundItemsProps): ReactElement => {
  const dispatch = useDispatch();
  const found = useSelector(getFoundItems(query));
  const fetching = useSelector(foundItemsFetching);

  useEffect(() => {
    dispatch(listFoundItems({ ...query, limit: FOUND_LIMIT }));
  }, [dispatch, query]);

  const rows = found.result ?? [];
  return (
    <Card id="found">
      {fetching && <LinearProgress />}
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" sx={{ mb: 0.5 }}>
          Found items
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Items marked Lost that a scan saw again. Each is back where it was seen.
          {found.total > FOUND_LIMIT ? ` Showing the latest ${FOUND_LIMIT} of ${found.total}.` : ''}
        </Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <MuiTable size="small" sx={{ minWidth: 640 }} data-testid="found-items">
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell>Found</TableCell>
                <TableCell>Where</TableCell>
                <TableCell>Marked lost</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary">
                      {fetching ? '' : 'No Lost item has been found in this range.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {rows.map((row) => (
                <TableRow key={`${row.item_id}-${row.payload_id}`} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ProductThumbnail imageKey={row.image_key} category={row.category_name} />
                      <MuiLink component={Link} to={`/tools/items/${row.item_id}/details`}>
                        {row.identifier}
                      </MuiLink>
                      <Typography variant="body2" color="text.secondary">
                        {row.product_name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <MuiLink component={Link} to={`/payloads/${row.payload_id}/details`}>
                      {savedWhen(row.found_at)}
                    </MuiLink>
                  </TableCell>
                  <TableCell>
                    {row.site_id && row.where !== 'vehicle' ? (
                      <MuiLink component={Link} to={`/customers/job-sites/${row.site_id}/details`}>
                        {foundWhere(row)}
                      </MuiLink>
                    ) : (
                      foundWhere(row)
                    )}
                  </TableCell>
                  <TableCell>{row.lost_at ? `${savedWhen(row.lost_at)}${row.lost_by ? ` by ${row.lost_by}` : ''}` : ''}</TableCell>
                  <TableCell>{row.unexpected_site && <Chip size="small" color="warning" variant="outlined" label="Unexpected site" />}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </MuiTable>
        </Box>
      </Box>
    </Card>
  );
};

export default FoundItems;
